/* eslint-disable */
import { showNotification } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { customAxios } from "lib/axios";

export type LeaveConfigLevel = "organization" | "department" | "program" | "user";

export type ServiceYearScale = {
  role: string;
  minYears: number;
  maxYears: number;
  days: number;
};

export type AnnualLeaveSettings = {
  enabled: boolean;
  baseAllocation: number;
  proRateByStartDate?: boolean;
  scaleByServiceYears?: boolean;
  serviceYearScale?: ServiceYearScale[];
};

export type SickLeaveSettings = {
  enabled: boolean;
  accrualRate?: number;
  maxAccrual?: number;
  gracePeriodDays?: number;
  requireCertification?: boolean;
  certificationAfterDays?: number;
};

export type PressingNecessitySettings = {
  enabled: boolean;
  daysPerYear?: number;
  proRated?: boolean;
};

export type CompassionateLeaveSettings = {
  enabled: boolean;
  maxDaysPerYear?: number;
};

export type BereavementLeaveSettings = {
  enabled: boolean;
  maxDaysPerOccurrence?: number;
};

export type GenericToggle = {
  enabled: boolean;
};

export type EducationLeaveSettings = {
  enabled: boolean;
  maxDaysPerYear?: number;
};

export type ProfessionalDevelopmentLeaveSettings = {
  enabled: boolean;
  maxDaysPerYear?: number;
};

export type CulturalSpiritualLeaveSettings = {
  enabled: boolean;
  maxDaysPerYear?: number;
};

export type LeaveWithoutPaySettings = {
  enabled: boolean;
  requiresApproval?: boolean;
};

export type ApprovalWorkflowSettings = {
  requireSupervisorApproval?: boolean;
  flagToDirectorThreshold?: number;
  autoApproveUnderDays?: number;
};

export type LeaveConfigurationInput = {
  org?: string;
  year?: number;
  level?: LeaveConfigLevel;
  entityId?: string | null;
  annualLeave?: AnnualLeaveSettings;
  sickLeave?: SickLeaveSettings;
  pressingNecessity?: PressingNecessitySettings;
  compassionateLeave?: CompassionateLeaveSettings;
  bereavementLeave?: BereavementLeaveSettings;
  juryDutyLeave?: GenericToggle;
  electionLeave?: { enabled: boolean; maxDaysPerYear?: number };
  bandBusinessLeave?: GenericToggle;
  governanceLeave?: GenericToggle;
  educationLeave?: EducationLeaveSettings;
  professionalDevelopmentLeave?: ProfessionalDevelopmentLeaveSettings;
  culturalSpiritualLeave?: CulturalSpiritualLeaveSettings;
  leaveWithoutPay?: LeaveWithoutPaySettings;
  approvalWorkflow?: ApprovalWorkflowSettings;
  createdBy?: string;
  updatedBy?: string;
};

export type LeaveConfiguration = LeaveConfigurationInput & {
  _id: string;
  createdAt?: string;
  updatedAt?: string;
};

export type LeaveConfigurationResponse = {
  success: boolean;
  message?: string;
  leaveConfiguration?: LeaveConfiguration;
  data?: {
    level?: string;
    entityId?: string;
    usersProcessed?: number;
    usersUpdated?: number;
    errors?: number;
  };
};

export type DeleteOverrideResponse = {
  success: boolean;
  message?: string;
  data?: {
    deletedLevel?: string;
    deletedEntityId?: string;
    usersRecalculated?: number;
    errors?: number;
  };
};

const updateLeaveConfiguration = async (
  id: string,
  data: LeaveConfigurationInput
): Promise<LeaveConfigurationResponse> => {
  const response: AxiosResponse<LeaveConfigurationResponse> =
    await customAxios.put(`/leave-configuration/${id}`, data);
  return response.data;
};

const deleteLeaveConfigOverride = async (
  orgId: string,
  level: LeaveConfigLevel,
  entityId: string
): Promise<DeleteOverrideResponse> => {
  const response: AxiosResponse<DeleteOverrideResponse> =
    await customAxios.delete(`/leave-configuration/${orgId}/override`, {
      data: { level, entityId },
    });
  return response.data;
};

export const useUpdateLeaveConfiguration = (p0: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    LeaveConfigurationResponse,
    Error,
    { id: string; data: LeaveConfigurationInput }
  >({
    mutationFn: ({ id, data }) => updateLeaveConfiguration(id, data),
    onSuccess: async () => {
      showNotification({
        title: "Leave configuration updated",
        message: "Leave configuration saved successfully.",
        color: "green",
      });

      // Invalidate all leave configuration related queries
      await queryClient.invalidateQueries({
        queryKey: ["leaveConfiguration"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["leaveConfigurationAll"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["leaveConfigurationResolved"],
      });
    },
  });
};

export const useDeleteLeaveConfigOverride = () => {
  const queryClient = useQueryClient();

  return useMutation<
    DeleteOverrideResponse,
    Error,
    { orgId: string; level: LeaveConfigLevel; entityId: string }
  >({
    mutationFn: ({ orgId, level, entityId }) =>
      deleteLeaveConfigOverride(orgId, level, entityId),
    onSuccess: async () => {
      showNotification({
        title: "Override deleted",
        message: "Leave configuration override removed. Users now use parent-level config.",
        color: "green",
      });

      await queryClient.invalidateQueries({
        queryKey: ["leaveConfiguration"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["leaveConfigurationAll"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["leaveConfigurationResolved"],
      });
    },
  });
};
